import React from 'react';
import {
  Typography,
  Container,
  Row as GridRow,
  Col,
  FlexColumn,
  Spacer,
  DashboardLayout,
  Divider,
  ScrollRegion,
} from 'design-system';

const Separator = ({ className = '' }) => (
  <div className={['separator', className].filter(Boolean).join(' ')} />
);

export default function GridsAndLayoutsShowcase() {
  return (
    <>
        <GridRow>
          <Col xs={12}>
            <div className="design-system-section">
              <Typography variant="h1" className="section-title mb-4">
                Grids and layouts
              </Typography>
              <Separator className="mb-5" />
              <Typography variant="h2" className="mb-3" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Layout shell
              </Typography>
              <Typography variant="body2" className="mb-3 text-muted">
                <code>DashboardLayout</code> — 240px sidebar, 32px gap, main with 32px trailing padding (1440px
                dashboard spec). Optional <code>stackOnSmall</code> stacks below 768px.
              </Typography>
              <div className="mb-5" style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                <DashboardLayout
                  sidebar={
                    <div style={{ background: '#E0E7FF', padding: 16, minHeight: 120 }}>
                      <Typography variant="body2">Sidebar</Typography>
                    </div>
                  }
                >
                  <div style={{ background: '#F3F4F6', padding: 16, minHeight: 120 }}>
                    <Typography variant="body1">Main content</Typography>
                  </div>
                </DashboardLayout>
              </div>
              <Typography variant="h2" className="mb-3" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Centered containers
              </Typography>
              <Typography variant="body1" className="mb-3">
                <code>maxWidth=&quot;md&quot;</code> (720px), <code>&quot;dashboard&quot;</code> (1244px),{' '}
                <code>&quot;dashboard-central&quot;</code> (800px), <code>&quot;desktop-grid&quot;</code> (1280px).
                Horizontal padding: 16px mobile, 24px from 768px up.
              </Typography>
              <Container maxWidth="md" className="mb-4" style={{ background: '#F3F4F6', padding: 16, borderRadius: 8 }}>
                <Typography variant="body1">
                  Content inside a centered container. Use <code>fluid</code> for full width.
                </Typography>
              </Container>
              <Typography variant="h2" className="mb-3 mt-4" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Column grid &amp; gutters
              </Typography>
              <Typography variant="body2" className="mb-3 text-muted">
                Row <code>gutter</code>: <code>default</code> / <code>condensed</code> (16px, Figma),{' '}
                <code>spacious</code> (32px), or numeric <code>16</code> / <code>24</code> / <code>32</code> (48 maps to
                32). <code>ds-col-xl-*</code> applies from 1280px (desktop grid).
              </Typography>
              <GridRow gutter="spacious">
                <Col xs={12} md={4}>
                  <div
                    style={{
                      padding: 12,
                      background: '#EEF2FF',
                      borderRadius: 8,
                    }}
                  >
                    <Typography variant="body2">Col md=4 (spacious gutter)</Typography>
                  </div>
                </Col>
                <Col xs={12} md={8}>
                  <div
                    style={{
                      padding: 12,
                      background: '#E0E7FF',
                      borderRadius: 8,
                    }}
                  >
                    <Typography variant="body2">Col md=8</Typography>
                  </div>
                </Col>
              </GridRow>
              <Typography variant="body2" className="mt-3 text-muted">
                Vertical rhythm: Spacer <code>size={'{4}'}</code> (numeric 0–6)
              </Typography>
              <Spacer size={4} />
              <div style={{ height: 4, background: '#C7D2FE', borderRadius: 2 }} />
              <Typography variant="h2" className="mb-3 mt-4" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Divider &amp; ScrollRegion
              </Typography>
              <Typography variant="body2" className="mb-3 text-muted">
                <code>Divider</code> uses <code>--ds-divider-color</code>. <code>ScrollRegion</code> is a flex-friendly
                scroll container (<code>min-height: 0</code>, <code>overflow-y: auto</code>) with default{' '}
                <code>--ds-scroll-region-max-height</code> — used in client rail and similar panels.
              </Typography>
              <Divider className="mb-3" />
              <FlexColumn
                fullWidth
                justify="start"
                align="stretch"
                gap={8}
                style={{
                  height: 200,
                  minHeight: 0,
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <Typography variant="body2" component="div">
                  Fixed 200px column — list below scrolls inside ScrollRegion.
                </Typography>
                <ScrollRegion style={{ flex: '1 1 auto', minHeight: 0, maxHeight: 'none' }}>
                  {Array.from({ length: 24 }, (_, i) => (
                    <Typography key={i} variant="body2">
                      Scroll line {i + 1}
                    </Typography>
                  ))}
                </ScrollRegion>
              </FlexColumn>
            </div>
          </Col>
        </GridRow>
    </>
  );
}